import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getSuperDashboard } from "../../../redux/actions/profile";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  AreaChart,
  Area,
} from "recharts";

const PRIMARY = "#a7cc48";
const BG = "#2c2d3f";
const COLORS = [
  "#a7cc48",
  "#4fb0c6",
  "#f4a261",
  "#e63946",
  "#9b5de5",
  "#2ec4b6",
];

function StatCard({ label, value, icon, sub, accent }) {
  const color = accent || PRIMARY;
  return (
    <div style={{ ...S.card, borderTop: "3px solid " + color }}>
      <div style={{ ...S.cardIcon, background: color + "18" }}>
        <i className={icon} style={{ fontSize: 22, color }} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={S.cardValue}>{value ?? "—"}</div>
        <div style={S.cardLabel}>{label}</div>
        {sub && <div style={S.cardSub}>{sub}</div>}
      </div>
    </div>
  );
}

function Section({ title, icon }) {
  return (
    <div style={S.sectionTitle}>
      <i className={icon} style={{ color: PRIMARY, marginRight: 8 }} />
      {title}
    </div>
  );
}

export default function GlobalOverview() {
  const dispatch = useDispatch();
  const data = useSelector((s) => s.profile.superDashboard);

  useEffect(() => {
    dispatch(getSuperDashboard());
  }, [dispatch]);

  if (!data) {
    return (
      <div style={S.loading}>
        <div style={S.spinner} />
        <p style={{ color: "#888", marginTop: 16, fontSize: 14 }}>Loading…</p>
      </div>
    );
  }

  const agentStatusData = [
    { name: "Approved", value: data.approvedAgents || 0 },
    { name: "Pending", value: data.pendingAgents || 0 },
  ];
  const walletData = [
    { name: "With Wallet", value: data.merchantsWithWallet || 0 },
    {
      name: "No Wallet",
      value: Math.max(
        0,
        (data.totalMerchants || 0) - (data.merchantsWithWallet || 0),
      ),
    },
  ];

  return (
    <div style={S.root}>
      <div style={S.pageHeader}>
        <div>
          <h2 style={S.heading}>App Dashboard</h2>
          <p style={S.headingSub}>Full platform overview</p>
        </div>
        <div style={S.liveTag}>
          <span style={S.liveDot} />
          Live Data
        </div>
      </div>

      <Section title='Users & Accounts' icon='bi bi-people-fill' />
      <div style={S.cardGrid}>
        <StatCard
          label='Total Merchants'
          value={data.totalMerchants?.toLocaleString()}
          icon='bi bi-shop'
        />
        <StatCard
          label='Active Subscriptions'
          value={data.activeMerchants?.toLocaleString()}
          icon='bi bi-patch-check-fill'
          accent='#4fb0c6'
          sub={
            (data.totalMerchants
              ? Math.round((data.activeMerchants / data.totalMerchants) * 100)
              : 0) + "% of total"
          }
        />
        <StatCard
          label='Total Agents'
          value={data.totalAgents?.toLocaleString()}
          icon='bi bi-person-badge-fill'
          accent='#f4a261'
          sub={
            data.approvedAgents +
            " approved · " +
            data.pendingAgents +
            " pending"
          }
        />
        <StatCard
          label='Sales Admins'
          value={data.totalSalesAdmins?.toLocaleString()}
          icon='bi bi-shield-person-fill'
          accent='#9b5de5'
        />
      </div>

      <Section title='Wallets & Finance' icon='bi bi-wallet2' />
      <div style={S.cardGrid}>
        <StatCard
          label='Merchants with Wallet'
          value={data.merchantsWithWallet?.toLocaleString()}
          icon='bi bi-credit-card-fill'
        />
        <StatCard
          label='Active Wallets'
          value={data.activeWallets?.toLocaleString()}
          icon='bi bi-check-circle-fill'
          accent='#4fb0c6'
        />
        <StatCard
          label='Total Wallets'
          value={data.totalWallets?.toLocaleString()}
          icon='bi bi-bank'
          accent='#f4a261'
        />
        <StatCard
          label='Total Wallet Balance'
          value={
            "₦" +
            (data.totalWalletBalance || 0).toLocaleString("en-NG", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
          }
          icon='bi bi-cash-stack'
          accent='#2ec4b6'
        />
      </div>

      <Section title='SeerBit Compliance' icon='bi bi-shield-check' />
      <div style={S.cardGrid}>
        <StatCard
          label='Paystack Wallets'
          value={data.paystackWalletCount?.toLocaleString()}
          icon='bi bi-credit-card-fill'
        />
        <StatCard
          label='SeerBit Wallets'
          value={data.seerbitWalletCount?.toLocaleString()}
          icon='bi bi-wallet2'
          accent='#9b5de5'
        />
        <StatCard
          label='Pending Tier Upgrades'
          value={data.pendingTierUpgrades?.toLocaleString()}
          icon='bi bi-shield-exclamation'
          accent='#f4a261'
        />
        <StatCard
          label='Pending Business Upgrades'
          value={data.pendingBusinessUpgrades?.toLocaleString()}
          icon='bi bi-building-exclamation'
          accent='#e63946'
        />
      </div>

      <Section title='Analytics & Trends' icon='bi bi-graph-up-arrow' />
      <div style={S.chartsRow}>
        <div style={S.chartCard}>
          <div style={S.chartTitle}>
            <i
              className='bi bi-person-plus-fill'
              style={{ color: PRIMARY, marginRight: 6 }}
            />
            Monthly Merchant Signups
          </div>
          <ResponsiveContainer width='100%' height={200}>
            <AreaChart data={data.monthlyMerchants || []}>
              <defs>
                <linearGradient id='grad' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='5%' stopColor={PRIMARY} stopOpacity={0.2} />
                  <stop offset='95%' stopColor={PRIMARY} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray='3 3' stroke='#f0f0f0' />
              <XAxis dataKey='month' tick={{ fill: "#888", fontSize: 12 }} />
              <YAxis
                tick={{ fill: "#888", fontSize: 12 }}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: 8,
                  border: "none",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                }}
              />
              <Area
                type='monotone'
                dataKey='merchants'
                stroke={PRIMARY}
                strokeWidth={2}
                fill='url(#grad)'
                dot={{ fill: PRIMARY, r: 4 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div style={S.chartCard}>
          <div style={S.chartTitle}>
            <i
              className='bi bi-pie-chart-fill'
              style={{ color: PRIMARY, marginRight: 6 }}
            />
            Agent Status
          </div>
          <ResponsiveContainer width='100%' height={200}>
            <PieChart>
              <Pie
                data={agentStatusData}
                cx='50%'
                cy='50%'
                innerRadius={50}
                outerRadius={80}
                dataKey='value'
                label={({ name, value }) => name + ": " + value}
              >
                {agentStatusData.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Legend />
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div style={S.chartCard}>
          <div style={S.chartTitle}>
            <i
              className='bi bi-wallet-fill'
              style={{ color: PRIMARY, marginRight: 6 }}
            />
            Wallet Adoption
          </div>
          <ResponsiveContainer width='100%' height={200}>
            <PieChart>
              <Pie
                data={walletData}
                cx='50%'
                cy='50%'
                innerRadius={50}
                outerRadius={80}
                dataKey='value'
                label={({ name, value }) => name + ": " + value}
              >
                <Cell fill={PRIMARY} />
                <Cell fill='#e0e0e0' />
              </Pie>
              <Legend />
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {data.planData && data.planData.length > 0 && (
        <div style={{ ...S.chartCard, marginBottom: 28 }}>
          <div style={S.chartTitle}>
            <i
              className='bi bi-bar-chart-fill'
              style={{ color: PRIMARY, marginRight: 6 }}
            />
            Subscription Plan Distribution
          </div>
          <ResponsiveContainer width='100%' height={200}>
            <BarChart data={data.planData}>
              <CartesianGrid strokeDasharray='3 3' stroke='#f0f0f0' />
              <XAxis dataKey='plan' tick={{ fill: "#888", fontSize: 12 }} />
              <YAxis
                tick={{ fill: "#888", fontSize: 12 }}
                allowDecimals={false}
              />
              <Tooltip contentStyle={{ borderRadius: 8, border: "none" }} />
              <Bar dataKey='count' radius={[6, 6, 0, 0]}>
                {(data.planData || []).map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {data.recentSignups && data.recentSignups.length > 0 && (
        <>
          <Section title='Recent Merchant Signups' icon='bi bi-clock-history' />
          <div style={S.recentTable}>
            <table style={S.table}>
              <thead>
                <tr>
                  {["Store / Email", "Plan", "Joined", "Last Login"].map(
                    (h) => (
                      <th key={h} style={S.th}>
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {data.recentSignups.map((m, i) => {
                  const name = m.storeInfo?.storeName || m.email;
                  const active =
                    m.subscriptionEnd && Date.now() < m.subscriptionEnd;
                  return (
                    <tr key={i} style={S.tr}>
                      <td style={S.td}>
                        <div
                          style={{ fontWeight: 600, color: BG, fontSize: 13 }}
                        >
                          {name}
                        </div>
                        {m.storeInfo?.storeName && (
                          <div style={{ fontSize: 11, color: "#999" }}>
                            {m.email}
                          </div>
                        )}
                      </td>
                      <td style={S.td}>
                        <span
                          style={{
                            background: active
                              ? "rgba(167,204,72,0.12)"
                              : "#f5f5f5",
                            color: active ? "#5a8a00" : "#888",
                            borderRadius: 20,
                            padding: "2px 10px",
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                        >
                          {m.plan || "Free"}
                        </span>
                      </td>
                      <td style={S.td}>
                        {m.createdAt
                          ? new Date(m.createdAt).toLocaleDateString()
                          : "—"}
                      </td>
                      <td style={S.td}>
                        {m.lastLogin
                          ? new Date(m.lastLogin).toLocaleString()
                          : "Never"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

const S = {
  root: { paddingBottom: 40 },
  loading: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 360,
  },
  spinner: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    border: "3px solid " + PRIMARY,
    borderTopColor: "transparent",
  },
  pageHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 28,
  },
  heading: { fontSize: 24, fontWeight: 800, color: BG, margin: 0 },
  headingSub: { fontSize: 13, color: "#888", marginTop: 4 },
  liveTag: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    background: "rgba(167,204,72,0.12)",
    border: "1px solid " + PRIMARY,
    borderRadius: 20,
    padding: "5px 14px",
    fontSize: 12,
    fontWeight: 600,
    color: "#5a8a00",
  },
  liveDot: { width: 7, height: 7, borderRadius: "50%", background: PRIMARY },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "#888",
    textTransform: "uppercase",
    letterSpacing: 1,
    marginBottom: 14,
    marginTop: 8,
    display: "flex",
    alignItems: "center",
  },
  cardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))",
    gap: 16,
    marginBottom: 32,
  },
  card: {
    background: "#fff",
    borderRadius: 10,
    padding: "18px 20px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
    display: "flex",
    alignItems: "flex-start",
    gap: 14,
  },
  cardIcon: {
    width: 46,
    height: 46,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  cardValue: { fontSize: 22, fontWeight: 800, color: BG, lineHeight: 1.2 },
  cardLabel: { fontSize: 12, color: "#888", marginTop: 3 },
  cardSub: { fontSize: 11, color: "#bbb", marginTop: 4 },
  chartsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: 20,
    marginBottom: 28,
  },
  chartCard: {
    background: "#fff",
    borderRadius: 10,
    padding: "20px 18px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
    marginBottom: 0,
  },
  chartTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: BG,
    marginBottom: 16,
    display: "flex",
    alignItems: "center",
  },
  recentTable: {
    background: "#fff",
    borderRadius: 10,
    overflowX: "auto",
    boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
    marginBottom: 8,
    WebkitOverflowScrolling: "touch",
  },
  table: { width: "100%", borderCollapse: "collapse", minWidth: 540 },
  th: {
    background: BG,
    color: "#fff",
    padding: "11px 14px",
    textAlign: "left",
    fontSize: 12,
    fontWeight: 600,
  },
  tr: { borderBottom: "1px solid #f5f5f5" },
  td: {
    padding: "11px 14px",
    fontSize: 13,
    color: "#555",
    verticalAlign: "middle",
  },
};
